import React from "react";

import { useNavigate } from "react-router-dom";

import { AnimatedNumber, ListItemMotion, Shimmer } from "../MotionPrimitives";
import Tooltip from "../Tooltip";

const VISIBLE_USERS = 4;

const INSET_PANEL_CLASS =
  "rounded-xl bg-transparent ring-1 ring-gray-300/70 dark:bg-white/[0.04] dark:ring-white/[0.06]";

const formatMetricWindow = (seconds, fallback) => {
  const value = Number(seconds);

  if (!Number.isFinite(value) || value <= 0) return fallback;
  if (value === 3600) return "Last Hour";
  if (value % 60 === 0) return `Last ${value / 60} min`;

  return `Last ${value} sec`;
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "Unknown";

  const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);

  if (diff < 5) return "Just now";
  if (diff < 60) return `${diff} seconds ago`;

  const minutes = Math.floor(diff / 60);

  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(diff / 3600);

  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.floor(diff / 86400);

  if (days === 1) return "1 day ago";

  return `${days} days ago`;
};

const getActivityDisplayName = (activity) => {
  if (activity?.name) return activity.name;
  if (activity?.firstName || activity?.lastName) {
    return `${activity.firstName || ""} ${activity.lastName || ""}`.trim();
  }
  if (activity?.email) return activity.email;

  return "Unknown User";
};

const getActivityInitials = (activity) => {
  const source = getActivityDisplayName(activity);

  if (!source || source === "Unknown User") return "?";

  return (
    source
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
};

const MetricInfoTooltip = ({ horizontalAlign = "center", text }) => (
  <Tooltip horizontalAlign={horizontalAlign} position="top" text={text}>
    <button
      aria-label="Metric definition"
      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:text-gray-500 dark:hover:text-gray-300"
      type="button"
      onClick={(event) => event.stopPropagation()}
    >
      <i className="fas fa-info-circle text-[11px]" />
    </button>
  </Tooltip>
);

const ClientActivityAvatar = ({ activity }) => {
  const url = activity?.avatarUrl || activity?.avatar_url;
  const name = getActivityDisplayName(activity);

  if (url) {
    return (
      <img
        alt={name}
        className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-gray-200 dark:ring-white/10"
        src={url}
      />
    );
  }

  return (
    <div
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-[11px] font-semibold text-white ring-2 ring-gray-200 dark:ring-white/10"
    >
      {getActivityInitials(activity)}
    </div>
  );
};

const ClientActivityPanel = ({
  clientActivity,
  loading,
  showHeading = true,
}) => {
  const navigate = useNavigate();

  const registeredActivity = (clientActivity?.recentActivity || [])
    .filter(
      (activity) =>
        !!activity?.registrationId &&
        (activity.name ||
          activity.firstName ||
          activity.lastName ||
          activity.email)
    )
    .slice()
    .sort((left, right) => {
      if (!!left.isOnline !== !!right.isOnline) {
        return left.isOnline ? -1 : 1;
      }

      const leftTime = left.lastActive
        ? new Date(left.lastActive).getTime()
        : 0;
      const rightTime = right.lastActive
        ? new Date(right.lastActive).getTime()
        : 0;

      return rightTime - leftTime;
    });
  const registeredOnline =
    clientActivity?.registeredAccountsOnline ??
    registeredActivity.filter((activity) => activity.isOnline).length;
  const displayActivity = [
    ...registeredActivity.filter((activity) => activity.isOnline),
    ...registeredActivity.filter((activity) => !activity.isOnline),
  ].slice(0, VISIBLE_USERS);
  const rowTemplate = `repeat(${VISIBLE_USERS}, minmax(0, 1fr))`;

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)] gap-1.5 overflow-hidden sm:gap-2">
      {showHeading ? (
        <h3 className="text-sm font-semibold tracking-tight text-gray-900 dark:text-white sm:text-base">
          Client Activity
        </h3>
      ) : null}

      <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
        <div className={`p-2 sm:p-2.5 ${INSET_PANEL_CLASS}`}>
          <div className="flex items-center gap-1.5">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-orange-500/10 ring-1 ring-orange-500/20 sm:h-8 sm:w-8">
              <i className="fas fa-user-circle text-sm text-orange-600 dark:text-orange-400 sm:text-base" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1">
                <p className="truncate text-[10px] text-gray-600 dark:text-gray-400 sm:text-[11px]">
                  Authenticated Clients
                </p>
                <MetricInfoTooltip
                  text={`Authenticated app clients with a heartbeat in the ${formatMetricWindow(
                    clientActivity.liveWindowSeconds,
                    "Last 2 min"
                  ).toLowerCase()} window. Together with no-auth clients, this equals Live App Clients.`}
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-lg font-bold leading-none text-orange-600 tabular-nums dark:text-orange-400 sm:text-xl">
                  {loading ? (
                    <Shimmer className="h-7 w-10 rounded-md" />
                  ) : (
                    <AnimatedNumber
                      value={clientActivity.authenticatedClients}
                    />
                  )}
                </span>
                <span className="text-[10px] leading-none text-gray-500 dark:text-gray-400 sm:text-[11px]">
                  {formatMetricWindow(
                    clientActivity.liveWindowSeconds,
                    "Last 2 min"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={`p-2 sm:p-2.5 ${INSET_PANEL_CLASS}`}>
          <div className="flex items-center gap-1.5">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-green-500/10 ring-1 ring-green-500/20 sm:h-8 sm:w-8">
              <i className="fas fa-smile text-sm text-green-600 dark:text-green-400 sm:text-base" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1">
                <p className="truncate text-[10px] text-gray-600 dark:text-gray-400 sm:text-[11px]">
                  No-Auth Clients
                </p>
                <MetricInfoTooltip
                  horizontalAlign="right"
                  text={`App clients without an authenticated account heartbeat in the ${formatMetricWindow(
                    clientActivity.liveWindowSeconds,
                    "Last 2 min"
                  ).toLowerCase()} window. Together with authenticated clients, this equals Live App Clients.`}
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-lg font-bold leading-none text-green-600 tabular-nums dark:text-green-400 sm:text-xl">
                  {loading ? (
                    <Shimmer className="h-7 w-10 rounded-md" />
                  ) : (
                    <AnimatedNumber value={clientActivity.noAuthClients} />
                  )}
                </span>
                <span className="text-[10px] leading-none text-gray-500 dark:text-gray-400 sm:text-[11px]">
                  {formatMetricWindow(
                    clientActivity.liveWindowSeconds,
                    "Last 2 min"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex h-full min-h-0 flex-col overflow-hidden ${INSET_PANEL_CLASS}`}
      >
        <div className="flex-shrink-0 border-b border-gray-200/70 px-3 py-2 dark:border-white/[0.06]">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {registeredOnline === 0
              ? "No registered users active"
              : `${registeredOnline} registered account${registeredOnline !== 1 ? "s" : ""} online now`}
          </p>
        </div>

        {loading ? (
          <div
            className="grid h-full min-h-0 flex-1 divide-y divide-gray-200/60 dark:divide-white/[0.05]"
            style={{ gridTemplateRows: rowTemplate }}
          >
            {Array.from({ length: VISIBLE_USERS }).map((_, index) => (
              <div
                className="flex h-full items-center px-3 py-2"
                key={`activity-loading-${index}`}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Shimmer className="h-2 w-2 flex-shrink-0 rounded-full" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Shimmer className="h-3 w-2/3 rounded-md" />
                      <Shimmer className="h-2.5 w-1/3 rounded-md" />
                    </div>
                  </div>
                  <Shimmer className="h-2.5 w-12 flex-shrink-0 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        ) : displayActivity.length > 0 ? (
          <div
            className="grid h-full min-h-0 flex-1 divide-y divide-gray-200/60 dark:divide-white/[0.05]"
            style={{ gridTemplateRows: rowTemplate }}
          >
            {Array.from({ length: VISIBLE_USERS }).map((_, index) => {
              const activity = displayActivity[index];

              if (!activity) {
                return (
                  <div
                    aria-hidden
                    className="h-full min-h-0"
                    key={`activity-slot-${index}`}
                  />
                );
              }

              const { registrationId } = activity;
              const goToProfile = () => {
                if (registrationId) {
                  navigate(`/customers/${registrationId}`);
                }
              };

              return (
                <ListItemMotion
                  aria-label={`View profile for ${getActivityDisplayName(activity)}`}
                  className={`flex h-full w-full items-center px-3 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.04] sm:px-3.5 sm:py-2.5 ${registrationId ? "cursor-pointer" : ""}`}
                  index={index}
                  key={registrationId || activity.id || index}
                  role={registrationId ? "button" : undefined}
                  tabIndex={registrationId ? 0 : undefined}
                  onClick={registrationId ? goToProfile : undefined}
                  onKeyDown={
                    registrationId
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            goToProfile();
                          }
                        }
                      : undefined
                  }
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2.5">
                      <div className="relative flex-shrink-0">
                        <ClientActivityAvatar activity={activity} />
                        {activity.isOnline ? (
                          <span
                            aria-hidden
                            className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-900"
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {getActivityDisplayName(activity)}
                        </p>
                        {activity.ipAddress ? (
                          <p className="truncate font-mono text-xs text-blue-500">
                            {activity.ipAddress}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p
                        className={`text-xs ${
                          activity.isOnline
                            ? "font-medium text-green-600 dark:text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        {activity.isOnline
                          ? "Online"
                          : formatTimeAgo(
                              activity.lastActive || activity.timestamp
                            )}
                      </p>
                    </div>
                  </div>
                </ListItemMotion>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center px-3 py-4 text-center">
            <div>
              <i className="fas fa-user-slash mb-2 text-xl text-gray-400" />
              <p className="text-sm text-gray-500">
                No registered users active
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Anonymous sessions are not shown here
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientActivityPanel;
